import {
  ChatAction,
  ChatError,
  ChatResponse,
  SiteBootstrap,
  fetchBootstrap,
  sendChat,
  demanderHumain,
  transcrire,
  API_BASE,
} from './api';
import { ouvrirCanal } from './temps-reel';
import { LecteurVoix } from './voix';
import { Micro } from './micro';
import { EcouteEnFlux, compterMots, estEcho, type EtatTour } from './micro-flux';
import { libelleReessai, peutReessayer } from './reessai';
import { collectGuidePhrases, noteApercu, retirerAnnonces } from './ciblage';
import {
  clearMarks,
  guideScrollToElement,
  highlightEl,
  highlightPhrase,
  isStudioEmbed,
  navigateTo,
  placeAwayFromRect,
  pointTo,
  registerAction,
  resolveTargetElement,
  runRegisteredAction,
  scrollTo,
} from './actions';
import { collectLandmarks, pageRegardee } from './page-context';
import { placerAupresDuPersonnage, type Boite, type Cote } from './placement';

const STYLES = `
:host { all: initial; }
* { box-sizing: border-box; font-family: "Segoe UI", system-ui, sans-serif; }
.wrap {
  position: fixed;
  z-index: 2147483645;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  width: 72px;
  transition: left 0.45s ease, top 0.45s ease;
}
.wrap > * { pointer-events: auto; }
.speech {
  position: absolute;
  width: max-content;
  max-width: min(300px, calc(100vw - 100px));
  /* Une URL ou un mot sans espace ne doit pas sortir de la bulle en largeur. */
  overflow-wrap: anywhere;
  /* Une réponse plus haute que l'écran défile (hauteur maximale posée par ancrer()). */
  overflow-y: auto;
  background: #0f1c22;
  color: #e8f1ef;
  border: 1px solid rgba(45, 212, 191, 0.45);
  border-radius: 18px;
  padding: 12px 14px;
  font-size: 13.5px;
  line-height: 1.45;
  box-shadow: 0 18px 44px rgba(0,0,0,0.4);
  animation: rise 0.3s ease;
  cursor: pointer;
}
.speech.side-left { right: 78px; left: auto; bottom: 10px; border-radius: 18px 18px 4px 18px; }
.speech.side-right { left: 78px; right: auto; bottom: 10px; border-radius: 18px 18px 18px 4px; }
.speech .who {
  display: block;
  color: #2dd4bf;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 4px;
  font-weight: 700;
}
.speech .hint {
  display: block;
  margin-top: 8px;
  font-size: 11px;
  color: #7eb8b0;
}
.character {
  width: 72px;
  height: 72px;
  border: none;
  background: transparent;
  cursor: grab;
  padding: 0;
  position: relative;
  /* Ombre neutre : le personnage est blanc, il doit rester lisible sur un site blanc. */
  filter: drop-shadow(0 10px 16px rgba(15, 23, 42, 0.28));
  animation: idle 3.2s ease-in-out infinite;
  touch-action: none;
}
.character:active { cursor: grabbing; }
.character.thinking { animation: think 0.9s ease-in-out infinite; }
.character.guiding { animation: guidePulse 0.8s ease-in-out 2; }
.character.parle { animation: guidePulse 0.6s ease-in-out infinite; }
.character svg { width: 72px; height: 72px; display: block; }
/* fill-box : chaque œil se referme sur son propre centre, pas sur celui du dessin. */
.eye { transform-box: fill-box; transform-origin: center; animation: blink 5s infinite; }
.panel {
  position: absolute;
  right: 78px;
  bottom: 0;
  width: min(360px, calc(100vw - 100px));
  height: min(460px, calc(100vh - 40px));
  background: linear-gradient(165deg, #0f1c22 0%, #13242c 55%, #0d1a20 100%);
  border: 1px solid rgba(126, 184, 176, 0.25);
  border-radius: 22px;
  box-shadow: 0 28px 70px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: rise 0.3s ease;
}
.panel.hidden { display: none; }
.head {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(126, 184, 176, 0.15);
  background: rgba(11, 20, 24, 0.65);
}
.head-avatar { width: 36px; height: 36px; flex-shrink: 0; }
.head-avatar svg { width: 100%; height: 100%; }
.head-copy { flex: 1; min-width: 0; }
.head h2 { margin: 0; font-size: 14px; color: #e8f1ef; font-weight: 700; }
.head p { margin: 2px 0 0; font-size: 11px; color: #7eb8b0; }
.head button { background: transparent; border: none; color: #8aa3a0; font-size: 20px; cursor: pointer; line-height: 1; }
.head button.voix { display: flex; align-items: center; padding: 4px; }
.head button.voix svg { width: 18px; height: 18px; }
.head button.voix[aria-pressed="true"] { color: #2dd4bf; }
.head button.voix[hidden] { display: none; }
.chips { display: flex; gap: 6px; padding: 10px 12px 0; overflow-x: auto; scrollbar-width: none; }
.chips::-webkit-scrollbar { display: none; }
.chip {
  flex: 0 0 auto; border: 1px solid rgba(45, 212, 191, 0.35);
  background: rgba(45, 212, 191, 0.08); color: #d7f5ef;
  border-radius: 999px; padding: 7px 11px; font-size: 12px; cursor: pointer; white-space: nowrap;
}
.chip:hover { background: rgba(45, 212, 191, 0.18); }
.messages { flex: 1; overflow-y: auto; padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; }
.msg {
  max-width: 90%; padding: 10px 12px; border-radius: 14px;
  font-size: 13.5px; line-height: 1.45; white-space: pre-wrap; word-break: break-word;
}
.msg.assistant { align-self: flex-start; background: rgba(45, 212, 191, 0.1); color: #e8f1ef; border: 1px solid rgba(45, 212, 191, 0.2); }
.msg.user { align-self: flex-end; background: #1a303a; color: #e8f1ef; border: 1px solid rgba(126, 184, 176, 0.18); }
/* Conseiller humain : meme place que l'IA, teinte distincte. La couleur seule
   ne porte jamais l'information — la mention « conseiller » l'accompagne. */
.msg.assistant.conseiller { background: rgba(250, 204, 21, 0.12); border-color: rgba(250, 204, 21, 0.35); }
.msg.systeme {
  align-self: center; background: transparent; color: #9fb7b3;
  font-size: 12px; font-style: italic; border: none; padding: 4px 8px;
}
.bandeau-humain {
  padding: 8px 12px; font-size: 12px; color: #fde68a;
  background: rgba(250, 204, 21, 0.1); border-bottom: 1px solid rgba(250, 204, 21, 0.25);
}
.demander-humain {
  margin: 0 12px 12px; padding: 8px 12px; border-radius: 999px; cursor: pointer;
  background: transparent; color: #9fb7b3; font-size: 12px;
  border: 1px dashed rgba(126, 184, 176, 0.35);
}
.demander-humain:hover { color: #e8f1ef; border-color: rgba(45, 212, 191, 0.5); }
.demander-humain[hidden] { display: none; }
.msg .meta { display: block; font-size: 11px; color: #5ecf9a; margin-top: 6px; }
.msg .note-apercu { display: block; font-size: 12px; font-style: italic; color: #b8cbc8; margin-top: 8px; }
.msg .retry {
  display: block; margin-top: 8px; border: 1px solid rgba(45, 212, 191, 0.45);
  background: rgba(45, 212, 191, 0.12); color: #d7f5ef;
  border-radius: 999px; padding: 6px 12px; font-size: 12px; cursor: pointer;
}
.msg .retry:hover:not(:disabled) { background: rgba(45, 212, 191, 0.24); }
.msg .retry:disabled { opacity: 0.6; cursor: default; }
.msg .body strong, .speech .body strong { font-weight: 700; color: #eafaf6; }
.msg .body em, .speech .body em { font-style: italic; }
.msg .body code, .speech .body code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; background: rgba(45,212,191,0.14);
  padding: 1px 5px; border-radius: 6px;
}
.composer {
  display: flex; gap: 8px; padding: 12px;
  border-top: 1px solid rgba(126, 184, 176, 0.15); background: rgba(11, 20, 24, 0.45);
}
.composer input {
  flex: 1; border-radius: 999px; border: 1px solid rgba(126, 184, 176, 0.25);
  background: rgba(11, 20, 24, 0.75); color: #e8f1ef; padding: 10px 14px; outline: none;
}
.composer input:focus { border-color: #2dd4bf; box-shadow: 0 0 0 3px rgba(45, 212, 191, 0.18); }
.composer button {
  border: none; border-radius: 999px; padding: 0 16px;
  background: linear-gradient(135deg, #2dd4bf, #14b8a6); color: #042f2e; font-weight: 700; cursor: pointer;
}
.composer button.micro {
  width: 40px; padding: 0; display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid rgba(126, 184, 176, 0.35); color: #9fb7b3;
}
.composer button.micro svg { width: 18px; height: 18px; }
.composer button.micro[hidden] { display: none; }
.composer button.micro[data-etat="ecoute"] {
  background: #dc2626; border-color: #dc2626; color: #fff; animation: ecoute 1.2s ease-in-out infinite;
}
.composer button.micro[data-etat="transcription"],
.composer button.micro[data-etat="connexion"] { opacity: 0.6; cursor: progress; border-color: #dc2626; color: #fca5a5; }
@keyframes ecoute { 0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.5); } 50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); } }
@keyframes rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes idle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
@keyframes think { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-3px) scale(1.04); } }
@keyframes guidePulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }
@keyframes blink { 0%, 92%, 100% { transform: scaleY(1); } 96% { transform: scaleY(0.1); } }
@media (max-width: 520px) {
  /* Largeur seulement : la position est calculée par ancrer(). Un transform
     centré se cumulerait avec elle et repousserait le panneau hors de l'écran. */
  .panel { width: min(360px, calc(100vw - 24px)); }
}
.character:focus-visible, .chip:focus-visible, .composer button:focus-visible, .msg .retry:focus-visible,
.head button:focus-visible, .speech:focus-visible {
  outline: 2px solid #5eead4;
  outline-offset: 2px;
}
@media (prefers-reduced-motion: reduce) {
  .character, .character.thinking, .character.guiding, .character.parle, .eye,
  .composer button.micro { animation: none !important; }
  .wrap { transition: none !important; }
  .speech, .panel { animation: none !important; }
  * { scroll-behavior: auto !important; }
}
`;

let exemplaireSvg = 0;

/**
 * Le personnage : sphère blanche, bande d'acier, visière en relief, yeux laqués.
 * Le volume tient aux dégradés et à l'ombre, sans image ni moteur 3D.
 *
 * Chaque exemplaire porte ses propres identifiants : le personnage est inséré
 * deux fois dans la même ombre (bouton et en-tête du panneau), et un `url(#id)`
 * en double désignerait les dégradés du premier, même masqué.
 */
function characterSvg(size = 72): string {
  const n = ++exemplaireSvg;
  const id = (nom: string) => `g${nom}${n}`;
  return `<svg viewBox="0 0 100 100" width="${size}" height="${size}" aria-hidden="true">
  <defs>
    <radialGradient id="${id('corps')}" cx="36%" cy="28%" r="78%">
      <stop offset="0%" stop-color="#ffffff"/><stop offset="45%" stop-color="#f3f4f6"/>
      <stop offset="78%" stop-color="#d9dbe0"/><stop offset="100%" stop-color="#b4b8bf"/>
    </radialGradient>
    <linearGradient id="${id('acier')}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#fbfbfc"/><stop offset="22%" stop-color="#c3c7cd"/>
      <stop offset="45%" stop-color="#868b93"/><stop offset="62%" stop-color="#e4e6e9"/><stop offset="100%" stop-color="#585d65"/>
    </linearGradient>
    <linearGradient id="${id('visiere')}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#fdfdfd"/><stop offset="70%" stop-color="#f1f2f4"/><stop offset="100%" stop-color="#e2e4e8"/>
    </linearGradient>
    <radialGradient id="${id('oeil')}" cx="40%" cy="35%" r="65%">
      <stop offset="0%" stop-color="#4b5563"/><stop offset="60%" stop-color="#111318"/><stop offset="100%" stop-color="#000"/>
    </radialGradient>
    <filter id="${id('flou')}" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="1.1"/></filter>
    <clipPath id="${id('sphere')}"><circle cx="50" cy="47" r="40.6"/></clipPath>
  </defs>
  <ellipse cx="50" cy="95" rx="22" ry="2.8" fill="rgba(15,23,42,0.16)"/>
  <circle cx="50" cy="47" r="40" fill="url(#${id('corps')})" stroke="rgba(15,23,42,0.12)" stroke-width="0.6"/>
  <rect x="9" y="44" width="82" height="6.4" rx="1.2" fill="url(#${id('acier')})" clip-path="url(#${id('sphere')})"/>
  <rect x="25.2" y="30.6" width="49.6" height="34" rx="10.8" fill="rgba(15,23,42,0.22)" filter="url(#${id('flou')})"/>
  <rect x="25.2" y="29.2" width="49.6" height="34" rx="10.8" fill="url(#${id('visiere')})" stroke="rgba(15,23,42,0.07)" stroke-width="0.5"/>
  <path d="M36.5 30h27" stroke="#fff" stroke-width="0.9" stroke-linecap="round" opacity="0.9"/>
  <g class="eye"><circle cx="37.8" cy="44.4" r="3.6" fill="url(#${id('oeil')})"/><circle cx="36.7" cy="43.2" r="0.9" fill="#fff" opacity="0.75"/></g>
  <g class="eye"><circle cx="62.2" cy="44.4" r="3.6" fill="url(#${id('oeil')})"/><circle cx="61.1" cy="43.2" r="0.9" fill="#fff" opacity="0.75"/></g>
  <path d="M46.4 50.9Q50 51.6 53.6 50.9Q53 53.6 50 53.6Q47 53.6 46.4 50.9Z" fill="#15171c"/>
</svg>`;
}

function sessionKey(siteId: string) {
  return `genious_session_${siteId}`;
}

export type WidgetApi = {
  open: () => void;
  close: () => void;
  siteId: string;
  /** Identifiant du visiteur pour la session en cours — RGPD, droit d'accès. */
  visitorId?: string;
};

/** Haut-parleur, au trait : hérite de la couleur du bouton (coupé / actif). */
const ICONE_MICRO = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0"/><path d="M12 18v3"/></svg>`;

const ICONE_VOIX = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/></svg>`;

export async function mountWidget(
  siteId: string,
  options: { voix?: boolean; micro?: boolean } = {}
): Promise<WidgetApi> {
  const host = document.createElement('div');
  host.id = 'genious-root';
  host.setAttribute('data-genious', 'true');
  document.documentElement.appendChild(host);

  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = STYLES;
  shadow.appendChild(style);

  const wrap = document.createElement('div');
  wrap.className = 'wrap';
  wrap.style.right = '18px';
  wrap.style.bottom = '18px';
  wrap.style.left = 'auto';
  wrap.style.top = 'auto';
  shadow.appendChild(wrap);

  const speech = document.createElement('div');
  speech.className = 'speech side-left';
  speech.style.display = 'none';
  wrap.appendChild(speech);

  const panel = document.createElement('div');
  panel.className = 'panel hidden';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Conversation avec GENIOUS');
  panel.setAttribute('aria-hidden', 'true');
  panel.innerHTML = `
    <div class="head">
      <div class="head-avatar">${characterSvg(36)}</div>
      <div class="head-copy">
        <h2 id="g-name">GENIOUS</h2>
        <p>Vendeur numérique · guidage sur le site</p>
      </div>
      <button type="button" class="voix" id="g-voix" aria-pressed="false"
        aria-label="Lire les réponses à voix haute" title="Lire les réponses à voix haute" hidden>${ICONE_VOIX}</button>
      <button type="button" aria-label="Fermer" id="g-close">×</button>
    </div>
    <div class="chips" id="g-chips"></div>
    <div class="bandeau-humain" id="g-humain" role="status" hidden></div>
    <div class="messages" id="g-messages" role="log" aria-live="polite"></div>
    <form class="composer" id="g-form">
      <input id="g-input" type="text" placeholder="Demandez un conseil ou une visite guidée…" autocomplete="off" />
      <button type="button" class="micro" id="g-micro" data-etat="repos"
        aria-label="Parler" title="Parler" hidden>${ICONE_MICRO}</button>
      <button type="submit">OK</button>
    </form>
    <button type="button" class="demander-humain" id="g-humain-btn">Parler à quelqu'un</button>
  `;
  wrap.appendChild(panel);

  const avatar = document.createElement('button');
  avatar.type = 'button';
  avatar.className = 'character';
  avatar.setAttribute('aria-label', 'Ouvrir la conversation avec GENIOUS');
  avatar.setAttribute('aria-haspopup', 'dialog');
  avatar.setAttribute('aria-expanded', 'false');
  avatar.innerHTML = characterSvg();
  wrap.appendChild(avatar);

  const messagesEl = panel.querySelector('#g-messages') as HTMLElement;
  const form = panel.querySelector('#g-form') as HTMLFormElement;
  const input = panel.querySelector('#g-input') as HTMLInputElement;
  const nameEl = panel.querySelector('#g-name') as HTMLElement;
  const closeBtn = panel.querySelector('#g-close') as HTMLButtonElement;
  const chipsEl = panel.querySelector('#g-chips') as HTMLElement;
  const bandeauHumain = panel.querySelector('#g-humain') as HTMLElement;
  const boutonHumain = panel.querySelector('#g-humain-btn') as HTMLButtonElement;
  const boutonVoix = panel.querySelector('#g-voix') as HTMLButtonElement;

  // Lecture à voix haute : coupée par défaut, même quand le site la propose.
  // Un site qui se met à parler sans qu'on l'ait demandé fait fuir.
  const lecteurVoix = options.voix
    ? new LecteurVoix(API_BASE, (parle) => {
        avatar.classList.toggle('parle', parle);
        surParoleAgent(parle);
      })
    : null;
  let voixActive = false;
  try {
    voixActive = Boolean(lecteurVoix) && sessionStorage.getItem(`genious_voix_${siteId}`) === '1';
  } catch {
    /* stockage indisponible : on repart coupé */
  }
  /** Allume ou coupe la lecture ; à appeler DANS un geste (déverrouillage audio). */
  function reglerVoix(active: boolean) {
    if (!lecteurVoix) return;
    voixActive = active && lecteurVoix.deverrouiller();
    boutonVoix.setAttribute('aria-pressed', voixActive ? 'true' : 'false');
    if (!voixActive) lecteurVoix.arreter();
    try {
      sessionStorage.setItem(`genious_voix_${siteId}`, voixActive ? '1' : '0');
    } catch {
      /* ignore */
    }
  }
  if (lecteurVoix) {
    boutonVoix.hidden = false;
    boutonVoix.setAttribute('aria-pressed', voixActive ? 'true' : 'false');
    boutonVoix.addEventListener('click', () => reglerVoix(!voixActive));
  }

  // Le micro (core-ai D46, D47). Conversation fluide par défaut : écoute en
  // continu, fin de phrase détectée seule, micro rouvert après la réponse, agent
  // interruptible. Repli « clic pour parler, clic pour envoyer » quand l'écoute
  // en continu n'est pas disponible (navigateur, API, socle).
  const boutonMicro = panel.querySelector('#g-micro') as HTMLButtonElement;
  const micro = options.micro && Micro.disponible() ? new Micro() : null;
  const conversationPossible = Boolean(options.micro) && EcouteEnFlux.disponible();
  let etatMicro: 'repos' | 'connexion' | 'conversation' | 'ecoute' | 'transcription' = 'repos';
  const placeholderSaisie = input.placeholder;

  function majMicro(etat: typeof etatMicro) {
    etatMicro = etat;
    boutonMicro.dataset.etat = etat === 'conversation' ? 'ecoute' : etat;
    const libelle =
      etat === 'conversation'
        ? 'Arrêter la conversation'
        : etat === 'connexion'
          ? 'Connexion au micro…'
          : etat === 'ecoute'
            ? 'Envoyer ce que j’ai dit'
            : etat === 'transcription'
              ? 'Je comprends…'
              : 'Parler';
    boutonMicro.setAttribute('aria-label', libelle);
    boutonMicro.title = libelle;
    input.placeholder =
      etat === 'conversation'
        ? 'Je vous écoute… parlez quand vous voulez'
        : etat === 'connexion'
          ? 'Un instant, j’ouvre le micro…'
          : etat === 'ecoute'
            ? 'Je vous écoute… cliquez à nouveau pour envoyer'
            : etat === 'transcription'
              ? 'Je comprends…'
              : placeholderSaisie;
  }

  // ── Conversation fluide ────────────────────────────────────────────────
  /** Sans parole du visiteur pendant ce temps, le micro se coupe (et l'écoute cesse d'être facturée). */
  const SILENCE_MAX_MS = 12_000;
  let ecoute: EcouteEnFlux | null = null;
  let texteTour = '';
  let enTour = false;
  /** Ce que l'agent dit ou vient de dire : sert à reconnaître son écho dans le micro. */
  let texteAgent = '';
  let questionEnAttente: string | null = null;
  let minuterieSilence = 0;
  let finDeTour = 0;

  function armerSilence() {
    window.clearTimeout(minuterieSilence);
    if (etatMicro !== 'conversation') return;
    minuterieSilence = window.setTimeout(() => {
      if (etatMicro === 'conversation' && !enTour && !thinking && !lecteurVoix?.parle) {
        arreterConversation();
        noteSysteme('Micro coupé faute de parole. Cliquez sur le micro pour reprendre la conversation.');
      }
    }, SILENCE_MAX_MS);
  }

  /** L'agent a fini de parler : c'est au visiteur — le délai de silence repart. */
  function surParoleAgent(parle: boolean) {
    if (!parle && etatMicro === 'conversation' && !thinking) armerSilence();
  }

  /** L'agent occupe la parole : il réfléchit, écrit ou parle. */
  const agentOccupe = () => thinking || Boolean(lecteurVoix?.parle);

  function surTexteEntendu(morceau: string) {
    texteTour += morceau;
    enTour = true;
    window.clearTimeout(minuterieSilence);
    // Interruption (lot 3) : deux vrais mots du visiteur pendant que l'agent
    // parle, et qui ne sont pas l'écho de ce qu'il dit — l'agent se tait.
    if (lecteurVoix?.parle && compterMots(texteTour) >= 2 && !estEcho(texteTour, texteAgent)) {
      lecteurVoix.arreter();
    }
    if (!lecteurVoix?.parle) input.value = texteTour.trimStart();
  }

  function surTour(etat: EtatTour) {
    if (etat === 'debut') {
      enTour = true;
      window.clearTimeout(minuterieSilence);
    } else if (etat === 'fin') {
      // Les derniers mots peuvent suivre la fin de tour de quelques dizaines de
      // millisecondes (mesuré) : on leur laisse le temps d'arriver.
      window.clearTimeout(finDeTour);
      finDeTour = window.setTimeout(clorePhrase, 200);
    }
  }

  function clorePhrase() {
    const phrase = texteTour.trim();
    texteTour = '';
    enTour = false;
    input.value = '';
    if (!phrase || compterMots(phrase) === 0) return armerSilence();
    // L'agent s'est entendu lui-même : on l'ignore.
    if (agentOccupe() && estEcho(phrase, texteAgent)) return armerSilence();
    if (thinking) {
      // Une réponse s'écrit encore : la question part dès qu'elle est finie.
      questionEnAttente = phrase;
      return;
    }
    void handleSend(phrase);
  }

  async function demarrerConversation() {
    // Qui parle veut qu'on lui réponde à voix haute. Ce clic est aussi le
    // geste qui déverrouille l'audio pour toute la conversation.
    if (lecteurVoix && !voixActive) reglerVoix(true);
    const sessionId = bootstrap?.sessionId;
    if (!sessionId) {
      noteSysteme('Le micro n’est pas disponible pour le moment. Écrivez votre question.');
      return;
    }
    majMicro('connexion');
    const url =
      `${API_BASE.replace(/^http/, 'ws')}/runtime/ecoute` +
      `?site=${encodeURIComponent(siteId)}&conversation=${encodeURIComponent(sessionId)}`;
    const session = new EcouteEnFlux();
    ecoute = session;
    texteTour = '';
    enTour = false;
    try {
      await session.demarrer(url, {
        pret: () => {
          if (ecoute !== session) return;
          majMicro('conversation');
          armerSilence();
        },
        texte: (morceau) => ecoute === session && surTexteEntendu(morceau),
        tour: (etat) => ecoute === session && surTour(etat),
        fin: (raison) => {
          if (ecoute !== session) return;
          const etaitPrete = etatMicro === 'conversation';
          ecoute = null;
          window.clearTimeout(minuterieSilence);
          majMicro('repos');
          if (raison === 'visiteur') return;
          if (!etaitPrete) {
            // L'écoute en continu n'a jamais été prête (API, socle ou jeton sans
            // écoute) : on retombe sur le clic pour parler, sans que le visiteur
            // ait à le savoir — ou on le lui dit, s'il n'y a pas de repli.
            if (micro) void basculerMicro();
            else noteSysteme('Le micro n’est pas disponible pour le moment. Écrivez votre question.');
            return;
          }
          noteSysteme('Le micro s’est coupé. Cliquez dessus pour reprendre la conversation.');
        },
      });
    } catch {
      ecoute = null;
      majMicro('repos');
      noteSysteme('Micro indisponible : autorisez-le dans votre navigateur, ou écrivez votre question.');
    }
  }

  function arreterConversation() {
    const session = ecoute;
    ecoute = null;
    window.clearTimeout(minuterieSilence);
    window.clearTimeout(finDeTour);
    texteTour = '';
    enTour = false;
    input.value = '';
    majMicro('repos');
    session?.arreter();
  }

  /** Après chaque réponse : la question dite pendant qu'elle s'écrivait, ou l'écoute. */
  function apresReponse(texte: string) {
    texteAgent = texte;
    if (etatMicro !== 'conversation') return;
    if (questionEnAttente) {
      const q = questionEnAttente;
      questionEnAttente = null;
      void handleSend(q);
      return;
    }
    if (!lecteurVoix?.parle) armerSilence();
  }

  function noteSysteme(texte: string) {
    const note = document.createElement('div');
    note.className = 'msg systeme';
    note.textContent = texte;
    messagesEl.appendChild(note);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  async function basculerMicro() {
    if (!micro || thinking || etatMicro === 'transcription') return;

    if (etatMicro === 'repos') {
      // Qui parle veut qu'on lui réponde à voix haute. Ce clic est aussi le
      // geste qui déverrouille l'audio pour la réponse.
      if (lecteurVoix && !voixActive) reglerVoix(true);
      try {
        await micro.demarrer(() => void basculerMicro());
        majMicro('ecoute');
      } catch {
        noteSysteme('Micro indisponible : autorisez-le dans votre navigateur, ou écrivez votre question.');
      }
      return;
    }

    majMicro('transcription');
    const enregistrement = await micro.arreter();
    const texte = enregistrement
      ? await transcrire(siteId, bootstrap?.sessionId || '', enregistrement)
      : '';
    majMicro('repos');
    if (texte === null) {
      noteSysteme('Je n’ai pas pu vous entendre. Réessayez, ou écrivez votre question.');
    } else if (!texte.trim()) {
      noteSysteme('Je n’ai rien entendu. Réessayez en parlant un peu plus près du micro.');
    } else {
      void handleSend(texte);
    }
  }

  if (conversationPossible || micro) {
    boutonMicro.hidden = false;
    boutonMicro.addEventListener('click', () => {
      if (etatMicro === 'conversation' || etatMicro === 'connexion') return arreterConversation();
      if (etatMicro === 'repos' && conversationPossible) return void demarrerConversation();
      void basculerMicro();
    });
  }

  let isOpen = false;
  let thinking = false;
  let welcomed = false;
  let bootstrap: SiteBootstrap | null = null;
  let dragMoved = false;
  let bubbleSide: 'left' | 'right' = 'left';

  const quickPrompts = [
    'Présentez-vous brièvement',
    'Montrez-moi les points clés',
    'Emmenez-moi à la page contact',
    'Que proposez-vous concrètement ?',
  ];

  /**
   * Etat de prise en main cote visiteur.
   *
   * Le widget n'en decide jamais : il le RECOIT, par la reponse HTTP ou par le
   * canal. Le deduire localement produirait deux verites divergentes des qu'un
   * operateur rend la main depuis le Studio.
   */
  let statutHumain: 'ai_active' | 'handover_requested' | 'human_active' | 'closed' = 'ai_active';
  let canal: { fermer: () => void } | null = null;

  function majBandeauHumain(operateur?: string) {
    if (statutHumain === 'human_active') {
      bandeauHumain.hidden = false;
      bandeauHumain.textContent = operateur
        ? `${operateur} a pris la suite de la conversation.`
        : 'Un conseiller a pris la suite de la conversation.';
      input.placeholder = 'Ecrivez au conseiller…';
      boutonHumain.hidden = true;
      return;
    }
    if (statutHumain === 'handover_requested') {
      bandeauHumain.hidden = false;
      bandeauHumain.textContent = 'Votre demande est transmise. Je reste la en attendant.';
      boutonHumain.hidden = true;
      input.placeholder = 'Demandez un conseil ou une visite guidee…';
      return;
    }
    bandeauHumain.hidden = true;
    boutonHumain.hidden = false;
    input.placeholder = 'Demandez un conseil ou une visite guidee…';
  }

  boutonHumain.addEventListener('click', () => {
    if (statutHumain !== 'ai_active') return;
    // Optimiste : le bandeau s'affiche tout de suite. Si l'appel echoue, le
    // visiteur peut toujours ECRIRE sa demande — la detection serveur l'attrape.
    statutHumain = 'handover_requested';
    majBandeauHumain();
    void demanderHumain(siteId, bootstrap?.sessionId || '');
  });

  function ouvrirCanalTempsReel() {
    const sessionId = bootstrap?.sessionId;
    if (!sessionId) return;
    canal?.fermer();
    canal = ouvrirCanal({
      publicId: siteId,
      sessionId,
      onEvenement: (evenement) => {
        if (evenement.type === 'statut') {
          statutHumain = evenement.statut;
          majBandeauHumain(evenement.operateur);
          return;
        }
        // Seuls les messages du CONSEILLER arrivent par le canal sans etre deja
        // a l'ecran : ceux du visiteur et de l'IA sont peints par `tenter`, et
        // les repeindre ici les afficherait deux fois.
        if (evenement.message.role === 'admin') {
          appendMessage(
            'admin',
            evenement.message.content,
            evenement.message.auteur ? `${evenement.message.auteur} · conseiller` : 'Conseiller'
          );
          messagesEl.scrollTop = messagesEl.scrollHeight;
        }
      },
    });
  }

  function renderChips() {
    chipsEl.innerHTML = '';
    for (const label of quickPrompts) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'chip';
      btn.textContent = label;
      btn.addEventListener('click', () => void handleSend(label));
      chipsEl.appendChild(btn);
    }
  }
  renderChips();

  function appendMessage(role: 'user' | 'assistant' | 'admin', content: string, metaText?: string) {
    const div = document.createElement('div');
    // Un message de conseiller prend l'allure d'une reponse (meme colonne, meme
    // bulle) mais garde sa propre classe : c'est ce qui permet de le distinguer
    // a l'oeil, et de ne jamais lui appliquer la mention « Reponse GENIOUS ».
    div.className = role === 'admin' ? 'msg assistant conseiller' : `msg ${role}`;
    const body = document.createElement('span');
    body.className = 'body';
    if (role === 'assistant') {
      renderRich(body, content);
    } else {
      // Un conseiller ecrit du texte, pas du balisage : l'echapper protege le
      // visiteur d'une injection venue du Studio.
      body.textContent = content;
    }
    div.appendChild(body);
    if (metaText) {
      const meta = document.createElement('span');
      meta.className = 'meta';
      meta.textContent = metaText;
      div.appendChild(meta);
    }
    messagesEl.appendChild(div);
    messagesEl.scrollTop = messagesEl.scrollHeight;
    return div;
  }

  function showSpeech(text: string, opts?: { guiding?: boolean }) {
    const name = bootstrap?.agent.name || 'GENIOUS';
    speech.className = `speech side-${bubbleSide}`;
    speech.innerHTML = '';
    const who = document.createElement('span');
    who.className = 'who';
    who.textContent = name;
    speech.appendChild(who);
    const body = document.createElement('span');
    body.className = 'body';
    renderRich(body, text);
    speech.appendChild(body);
    if (opts?.guiding) {
      const hint = document.createElement('span');
      hint.className = 'hint';
      hint.textContent = 'Cliquez pour ouvrir la conversation';
      speech.appendChild(hint);
    }
    speech.style.display = 'block';
    repositionner();
  }

  function hideSpeech() {
    speech.style.display = 'none';
  }

  /**
   * La boîte du personnage, là où il VA être.
   *
   * `.wrap` anime `left` et `top` sur 0,45 s : juste après un déplacement guidé,
   * `getBoundingClientRect()` rend la position de DÉPART de l'animation. Placer
   * la bulle d'après elle la mettrait à côté d'un personnage qui n'y est plus.
   * La cible, elle, est écrite dans le style ; seul le placement par défaut
   * (`right` / `bottom`) n'en a pas, et il n'est pas animé.
   */
  function boitePersonnage(): Boite {
    const rendu = wrap.getBoundingClientRect();
    const left = parseFloat(wrap.style.left);
    const top = parseFloat(wrap.style.top);
    return {
      left: Number.isFinite(left) ? left : rendu.left,
      top: Number.isFinite(top) ? top : rendu.top,
      largeur: rendu.width,
      hauteur: rendu.height,
    };
  }

  /**
   * Place la bulle ou le panneau dans l'écran, d'après sa taille RÉELLE
   * (voir `placement.ts`). Rend le côté retenu, ou `null` si l'élément est masqué.
   */
  function ancrer(element: HTMLElement, cote: Cote, decalageBas: number): Cote | null {
    // Mesurer la taille naturelle : une hauteur maximale posée par un placement
    // précédent ferait paraître le contenu déjà écourté.
    element.style.maxHeight = '';
    const contenu = { largeur: element.offsetWidth, hauteur: element.offsetHeight };
    if (!contenu.largeur) return null;

    const perso = boitePersonnage();
    const place = placerAupresDuPersonnage(
      perso,
      contenu,
      { largeur: window.innerWidth, hauteur: window.innerHeight },
      { cote, decalageBas }
    );

    // L'élément est positionné par rapport au conteneur, lui-même en `position: fixed`.
    element.style.left = `${place.left - perso.left}px`;
    element.style.top = `${place.top - perso.top}px`;
    element.style.right = 'auto';
    element.style.bottom = 'auto';
    element.style.maxHeight = place.maxHauteur === null ? '' : `${place.maxHauteur}px`;
    return place.cote;
  }

  /** Remet dans l'écran tout ce qui est accroché au personnage et visible. */
  function repositionner() {
    if (speech.style.display === 'block') {
      const cote = ancrer(speech, bubbleSide === 'left' ? 'gauche' : 'droite', 10);
      // La « queue » de la bulle suit le côté réellement retenu.
      if (cote) speech.className = `speech side-${cote === 'gauche' ? 'left' : 'right'}`;
    }
    if (isOpen) ancrer(panel, 'gauche', 0);
  }

  /** Au plus un repositionnement par image : glisser-déposer et redimensionnement. */
  let repositionnementPrevu = 0;
  function prevoirRepositionnement() {
    if (repositionnementPrevu) return;
    repositionnementPrevu = requestAnimationFrame(() => {
      repositionnementPrevu = 0;
      repositionner();
    });
  }
  window.addEventListener('resize', prevoirRepositionnement);

  function setOpen(next: boolean) {
    isOpen = next;
    panel.classList.toggle('hidden', !isOpen);
    panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    avatar.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    if (isOpen) {
      hideSpeech();
      // Message d'accueil affiché une seule fois à la première ouverture.
      if (!welcomed && !messagesEl.childElementCount) {
        welcomed = true;
        const welcome = bootstrap?.agent.welcome;
        if (welcome) appendMessage('assistant', welcome);
      }
      repositionner();
      input.focus();
    } else {
      avatar.focus();
    }
  }

  function positionNearEl(el: Element | null) {
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const placed = placeAwayFromRect(wrap, rect, 300, 140);
    bubbleSide = placed.bubbleSide;
    repositionner();
    avatar.classList.add('guiding');
    window.setTimeout(() => avatar.classList.remove('guiding'), 1400);
    return el;
  }

  function guideToElement(target: Element) {
    setOpen(false);
    guideScrollToElement(target);
    highlightEl(target, 7000);
    positionNearEl(target);
    for (const delay of [180, 420, 700, 1100, 1550]) {
      window.setTimeout(() => positionNearEl(target), delay);
    }
  }

  async function applyActions(actions: ChatAction[] | undefined, reply: string) {
    if (!actions?.length) return false;

    const studio = isStudioEmbed();

    try {
      const phrases = collectGuidePhrases(actions, reply);

      // 1) Si l'IA demande explicitement une navigation, on la respecte en priorité.
      // Cela réactive le comportement cross-page (cas clé du produit).
      const nav = actions.find((a) => a.type === 'navigate' && a.href);
      if (nav?.href) {
        const guide = actions.find(
          (a) => (a.type === 'point' || a.type === 'highlight') && (a.phrase || a.selector)
        );
        const phrase = guide?.phrase ?? nav.phrase ?? phrases[0];
        const selector = guide?.selector ?? nav.selector;

        if (studio) {
          setOpen(false);
          // Le cadre change de page ; pointer dedans est interdit (autre origine).
          // L'ancien « réessai après chargement » cherchait la phrase dans la page
          // du Studio, jamais dans le site : il ne pouvait surligner que le Studio.
          const ok = navigateTo(nav.href);
          showSpeech(
            ok ? reply : `${reply}\n\n(Aperçu : je ne peux pas changer la page de votre site ici.)`,
            { guiding: true }
          );
          return true;
        }

        try {
          sessionStorage.setItem(
            `genious_pending_bubble_${siteId}`,
            JSON.stringify({
              reply,
              at: Date.now(),
              phrase,
              phrases,
              selector,
            })
          );
          sessionStorage.setItem(sessionKey(siteId), bootstrap?.sessionId || '');
        } catch {
          /* ignore */
        }
        showSpeech('Je vous emmène sur la bonne page…', { guiding: true });
        navigateTo(nav.href);
        return true;
      }

      // Aperçu du Studio : le site est dans un cadre d'une autre origine, où le
      // navigateur interdit tout geste. Chercher la phrase dans la page du Studio
      // risquerait de surligner le Studio lui-même : `tenter` décrit le geste.
      if (studio) return false;

      // 2) Guidage sur la page courante (scroll + surbrillance + positionnement).
      for (const phrase of phrases) {
        const target = resolveTargetElement({ phrase });
        if (target) {
          guideToElement(target);
          showSpeech(reply, { guiding: true });
          return true;
        }
      }
      for (const action of actions) {
        if (action.type === 'custom' && action.name) {
          await runRegisteredAction(action.name, action.payload);
          continue;
        }
        const target = resolveTargetElement({
          selector: action.selector,
          phrase: action.phrase,
        });
        if (!target) continue;
        guideToElement(target);
        showSpeech(reply, { guiding: true });
        return true;
      }

      return false;
    } catch (err) {
      console.warn('[GENIOUS] Action guidage interrompue', err);
      showSpeech(reply, { guiding: true });
      return true;
    }
  }

  function pageContext() {
    return {
      ...pageRegardee(),
      landmarks: collectLandmarks().slice(0, 45),
    };
  }

  /** Échéance du bouton « Réessayer » désactivé par une limite de débit. */
  let echeanceReessai = 0;

  /**
   * Une nouvelle question rend caduque la précédente restée sans réponse : la
   * réessayer après coup placerait sa réponse sous une question plus récente.
   */
  function retirerBoutonsReessai() {
    window.clearTimeout(echeanceReessai);
    messagesEl.querySelectorAll('.retry').forEach((bouton) => bouton.remove());
  }

  async function handleSend(text: string) {
    const trimmed = text.trim();
    if (!trimmed || thinking) return;
    retirerBoutonsReessai();
    hideSpeech();
    setOpen(true);
    appendMessage('user', trimmed);
    input.value = '';
    await tenter(trimmed, appendMessage('assistant', '…'));
  }

  /**
   * Rouvre une session quand la précédente est perdue : API injoignable au
   * chargement, ou conversation purgée par la rétention. Le personnage garde sa
   * configuration ; seule la session change.
   */
  async function reouvrirSession() {
    const nouvelle = await fetchBootstrap(siteId);
    if (!nouvelle.sessionId) return;
    bootstrap = bootstrap
      ? { ...bootstrap, sessionId: nouvelle.sessionId, visitorId: nouvelle.visitorId }
      : nouvelle;
    try {
      sessionStorage.setItem(sessionKey(siteId), nouvelle.sessionId);
    } catch {
      /* ignore */
    }
  }

  /**
   * Affiche l'échec dans la bulle de réponse, avec un bouton pour renvoyer la
   * MÊME question dans la MÊME bulle — sans la retaper, sans la réafficher.
   * L'API ne l'enregistre pas une seconde fois (`questionDejaEnregistree`).
   */
  function afficherEchec(assistantEl: HTMLElement, question: string, erreur: ChatError) {
    assistantEl.textContent = errorMessageFor(erreur.kind);
    assistantEl.setAttribute('data-state', erreur.kind);
    if (!peutReessayer(erreur.kind)) return;

    const bouton = document.createElement('button');
    bouton.type = 'button';
    bouton.className = 'retry';
    bouton.textContent = libelleReessai(erreur.retryAfterMs);
    if (erreur.retryAfterMs > 0) {
      bouton.disabled = true;
      echeanceReessai = window.setTimeout(() => {
        bouton.disabled = false;
        bouton.textContent = libelleReessai(0);
      }, erreur.retryAfterMs);
    }
    bouton.addEventListener('click', () => {
      if (thinking || bouton.disabled) return;
      retirerBoutonsReessai();
      assistantEl.removeAttribute('data-state');
      assistantEl.textContent = '…';
      void tenter(question, assistantEl, erreur.kind === 'session');
    });
    assistantEl.appendChild(bouton);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  async function tenter(question: string, assistantEl: HTMLElement, sessionARouvrir = false) {
    // Une nouvelle question coupe la voix de la précédente. Et ce clic d'envoi
    // déverrouille l'audio quand la voix est restée active après un
    // rechargement : on est encore dans le geste, avant le premier `await`.
    if (lecteurVoix && voixActive) {
      lecteurVoix.arreter();
      lecteurVoix.deverrouiller();
    }
    thinking = true;
    avatar.classList.add('thinking');
    // Lecteurs d'écran : la zone des messages est un journal annoncé. Pendant le
    // flux, la réponse est « occupée » : elle sera lue une fois complète, et non
    // mot à mot au fil des fragments.
    assistantEl.setAttribute('aria-busy', 'true');

    // Affichage progressif (W3-2). Les fragments remplacent le « … » et repeignent
    // le message ; la finalisation plus bas le repeint une dernière fois avec le
    // contenu CANONIQUE de l'API, qui peut différer (texte de repli, réécriture
    // « voilà, je vous montre »). Le texte du flux n'est jamais la vérité finale.
    let texteRecu = '';
    let corpsProgressif: HTMLSpanElement | null = null;
    // Repeint au plus une fois par image : un rendu complet par jeton ferait
    // reconstruire tout le message des dizaines de fois par seconde.
    let repeinture = 0;
    const surFragment = (texte: string) => {
      texteRecu += texte;
      // Ce que l'agent va dire : son écho dans le micro sera reconnu (lot 3).
      texteAgent = texteRecu;
      if (!corpsProgressif) {
        assistantEl.textContent = '';
        corpsProgressif = document.createElement('span');
        corpsProgressif.className = 'body';
        assistantEl.appendChild(corpsProgressif);
      }
      if (repeinture) return;
      repeinture = requestAnimationFrame(() => {
        repeinture = 0;
        if (corpsProgressif) {
          // `renderRich` échappe tout : un fragment reste du texte, jamais du HTML.
          renderRich(corpsProgressif, texteRecu);
          messagesEl.scrollTop = messagesEl.scrollHeight;
        }
      });
    };

    try {
      let res: ChatResponse;
      try {
        if (sessionARouvrir) await reouvrirSession();
        // La voix vient dans le flux même du chat, phrase par phrase (lot 1).
        const lecteur = lecteurVoix && voixActive ? lecteurVoix : null;
        res = await sendChat(siteId, bootstrap?.sessionId || '', question, {
          pageContext: pageContext(),
          onFragment: surFragment,
          voix: lecteur
            ? {
                signal: lecteur.commencerFlux(),
                surAudio: (audio) => lecteur.ajouterPcm16(audio),
                surStop: () => lecteur.arreter(),
                surFin: () => lecteur.terminerFlux(),
              }
            : undefined,
        });
      } catch (err) {
        if (repeinture) {
          cancelAnimationFrame(repeinture);
          repeinture = 0;
        }
        afficherEchec(assistantEl, question, err instanceof ChatError ? err : new ChatError('server', String(err)));
        return;
      }
      // Une repeinture encore en attente écraserait le contenu canonique posé
      // juste après par un état intermédiaire du flux.
      if (repeinture) {
        cancelAnimationFrame(repeinture);
        repeinture = 0;
      }
      try {
        sessionStorage.setItem(sessionKey(siteId), bootstrap?.sessionId || '');
      } catch {
        /* ignore */
      }

      // Un conseiller tient la main : l'IA s'est tue exprès. Peindre une bulle
      // de reponse vide ferait croire a une panne ; on accuse reception et on
      // laisse la place a la reponse humaine, qui arrivera par le canal.
      if (res.enAttenteHumain) {
        statutHumain = 'human_active';
        majBandeauHumain();
        assistantEl.remove();
        const note = document.createElement('div');
        note.className = 'msg systeme';
        note.textContent = res.operateurEnLigne
          ? 'Message transmis au conseiller.'
          : 'Message transmis. Le conseiller le verra des sa prochaine connexion.';
        messagesEl.appendChild(note);
        messagesEl.scrollTop = messagesEl.scrollHeight;
        return;
      }

      // Demande d'humain reconnue dans le texte : le bandeau suit, sans attendre
      // un evenement du canal qui pourrait ne jamais arriver si la socket est
      // tombee.
      if (res.demandeHumainRecue && statutHumain === 'ai_active') {
        statutHumain = 'handover_requested';
        majBandeauHumain();
      }

      const reply = cleanReply(res.reply);
      texteAgent = reply;
      assistantEl.textContent = '';
      const body = document.createElement('span');
      body.className = 'body';
      renderRich(body, reply);
      assistantEl.appendChild(body);
      // Repli : l'API a répondu d'un bloc, sans flux (texte de repli, API plus
      // ancienne). La réponse se relit alors après coup, avant le guidage.
      if (lecteurVoix && voixActive && !res.voixEnFlux && res.messageId) {
        void lecteurVoix.lire(siteId, bootstrap?.sessionId || '', res.messageId);
      }
      const guided = await applyActions(res.actions, reply);
      const meta = document.createElement('span');
      meta.className = 'meta';
      // Plus de « Réponse ancrée sur votre site » : `grounded` vaut « au moins une
      // citation », et la recherche en rend toujours (recette modelabs du
      // 2026-10-01 : 26 réponses sur 26, inventions comprises). Un gage de
      // fiabilité qu'on ne sait pas tenir ne s'affiche pas. Il reviendra quand
      // une vérification réelle de la réponse existera (PLAN-QUALITE-RAG lot 5).
      meta.textContent = guided ? 'Guidage sur le site (bulle près du personnage)' : 'Réponse GENIOUS';
      assistantEl.appendChild(meta);

      // Aperçu du Studio : décrire le geste impossible au lieu de le taire.
      const note = !guided && isStudioEmbed() ? noteApercu(res.actions ?? [], reply) : null;
      if (note) {
        const noteEl = document.createElement('span');
        noteEl.className = 'note-apercu';
        noteEl.textContent = note;
        assistantEl.insertBefore(noteEl, meta);
      }

      // Rien n'a pu être montré : retirer du texte ce qui l'annonçait. Seul le
      // widget sait si le passage visé a été trouvé sur la page. Dans l'aperçu,
      // la note posée plus haut dit déjà ce qui aurait été montré.
      if (!guided && !isStudioEmbed()) {
        const net = retirerAnnonces(reply);
        if (net !== reply) {
          body.replaceChildren();
          renderRich(
            body,
            net || 'Je n’ai pas trouvé ce passage sur cette page. Dites-m’en un peu plus, je vous oriente.'
          );
        }
      }
    } catch {
      // Panne APRÈS la réponse (rendu, guidage) : la question a déjà eu sa
      // réponse, la renvoyer en produirait une seconde. Pas de bouton ici.
      assistantEl.textContent = errorMessageFor('server');
      assistantEl.setAttribute('data-state', 'server');
    } finally {
      if (repeinture) cancelAnimationFrame(repeinture);
      thinking = false;
      avatar.classList.remove('thinking');
      assistantEl.removeAttribute('aria-busy');
      // Conversation : la question dite pendant l'écriture part maintenant, ou
      // l'écoute reprend la main.
      apresReponse(texteAgent);
    }
  }

  function errorMessageFor(kind: string): string {
    switch (kind) {
      case 'quota':
        return 'GENIOUS a atteint sa limite de messages pour cette période. Revenez un peu plus tard.';
      case 'origin':
        return 'GENIOUS n’est pas encore autorisé sur ce domaine. Vérifiez l’installation dans le Studio.';
      case 'rate':
        return 'Un instant — trop de messages d’un coup. Réessayez dans quelques secondes.';
      case 'offline':
        return 'Connexion indisponible. Le site reste utilisable — je reviens dès que possible.';
      case 'session':
        return 'La conversation a été interrompue. Réessayez : j’en ouvre une nouvelle.';
      default:
        return 'Petit souci technique de mon côté. Réessayez dans un instant.';
    }
  }

  // Drag
  let dragging = false;
  let startX = 0;
  let startY = 0;
  let originLeft = 0;
  let originTop = 0;

  avatar.addEventListener('pointerdown', (e) => {
    dragging = true;
    dragMoved = false;
    avatar.setPointerCapture(e.pointerId);
    const rect = wrap.getBoundingClientRect();
    originLeft = rect.left;
    originTop = rect.top;
    startX = e.clientX;
    startY = e.clientY;
  });
  avatar.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (Math.abs(dx) + Math.abs(dy) > 4) dragMoved = true;
    wrap.style.left = `${Math.min(window.innerWidth - 80, Math.max(8, originLeft + dx))}px`;
    wrap.style.top = `${Math.min(window.innerHeight - 90, Math.max(8, originTop + dy))}px`;
    wrap.style.right = 'auto';
    wrap.style.bottom = 'auto';
    prevoirRepositionnement();
  });
  avatar.addEventListener('pointerup', () => {
    dragging = false;
  });
  avatar.addEventListener('click', () => {
    if (dragMoved) return;
    setOpen(!isOpen);
  });
  closeBtn.addEventListener('click', () => setOpen(false));
  speech.addEventListener('click', () => setOpen(true));

  // Accessibilité : Échap ferme le panneau / la bulle.
  wrap.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (isOpen) {
        setOpen(false);
      } else if (speech.style.display === 'block') {
        hideSpeech();
      }
    }
  });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    void handleSend(input.value);
  });

  // Bootstrap + reprise de session après navigation
  let storedSession: string | null = null;
  try {
    storedSession = sessionStorage.getItem(sessionKey(siteId));
  } catch {
    storedSession = null;
  }

  bootstrap = await fetchBootstrap(siteId, storedSession || undefined);
  if (bootstrap.sessionId) {
    try {
      sessionStorage.setItem(sessionKey(siteId), bootstrap.sessionId);
    } catch {
      /* ignore */
    }
  }
  nameEl.textContent = bootstrap.agent.name || 'GENIOUS';

  // Historique repeint AVANT toute ouverture : `setOpen` n'ajoute le message
  // d'accueil que si la liste est vide, donc cet ordre suffit à éviter le
  // doublon « accueil + conversation déjà commencée ».
  //
  // Aucune action n'est rejouée : elles ne sont pas stockées, et c'est voulu —
  // un scroll ou un surlignage rejoué au rechargement ferait bouger la page sous
  // le visiteur sans qu'il ait rien demandé. Un message restauré porte donc la
  // mention neutre, jamais « Guidage sur le site ».
  for (const m of bootstrap.messages) {
    appendMessage(
      m.role,
      m.content,
      // Même règle qu'en direct : pas de mention « ancrée » qu'on ne sait pas tenir.
      m.role === 'admin' ? 'Conseiller' : undefined
    );
  }

  // Canal temps reel : ouvert APRES la restitution de l'historique. L'inverse
  // ferait arriver un message de conseiller avant le fil qui le precede, donc
  // dans le desordre a l'ecran.
  ouvrirCanalTempsReel();
  majBandeauHumain();

  // Bulle pending après navigation
  try {
    const raw = sessionStorage.getItem(`genious_pending_bubble_${siteId}`);
    if (raw) {
      sessionStorage.removeItem(`genious_pending_bubble_${siteId}`);
      const pending = JSON.parse(raw) as {
        reply?: string;
        at?: number;
        phrase?: string;
        phrases?: string[];
        selector?: string;
      };
      if (pending.reply && pending.at && Date.now() - pending.at < 60_000) {
        setOpen(false);
        showSpeech(pending.reply, { guiding: true });
        const tryPhrases = [
          pending.phrase,
          ...(pending.phrases || []),
          ...collectGuidePhrases([], pending.reply),
        ].filter((p): p is string => Boolean(p && p.trim().length >= 3));

        const doHighlight = () => {
          let target: HTMLElement | null = null;
          for (const p of tryPhrases) {
            target = resolveTargetElement({ phrase: p, selector: pending.selector });
            if (target) break;
          }
          if (!target && pending.selector) {
            target = resolveTargetElement({ selector: pending.selector });
          }
          // Dernier repli : titre principal (mieux que rien, mais on a déjà tout tenté).
          if (!target) {
            target = document.querySelector('h1, h2, h3, [data-genious-target]') as HTMLElement | null;
          }
          if (target) {
            guideScrollToElement(target);
            highlightEl(target, 7000);
            for (const delay of [0, 250, 550, 900]) {
              window.setTimeout(
                () => {
                  placeAwayFromRect(wrap, target!.getBoundingClientRect(), 300, 140);
                  repositionner();
                },
                delay
              );
            }
          }
        };
        // Laisse la page (et une éventuelle SPA) finir son rendu.
        window.setTimeout(doHighlight, 350);
        window.setTimeout(doHighlight, 900);
      }
    }
  } catch {
    /* ignore */
  }

  const bubbleTexts = bootstrap.agent.bubbles?.length
    ? bootstrap.agent.bubbles
    : [
        'Je peux vous emmener sur la bonne page.',
        'Besoin d’un conseil avant de décider ?',
        'Je connais ce site — posez-moi une question.',
      ];

  let bubbleIdx = 0;
  const showProactive = () => {
    if (isOpen || speech.style.display === 'block') return;
    const text = bubbleTexts[bubbleIdx % bubbleTexts.length];
    bubbleIdx += 1;
    showSpeech(text);
  };

  // `online` (session jamais obtenue) et `siteStatus` (site pas encore indexé,
  // ou crawl en erreur) sont renvoyés par l'API précisément pour ce choix —
  // les ignorer ferait défiler les bulles marketing habituelles (« je connais
  // ce site ») sur un agent qui ne peut pas encore tenir cette promesse.
  const pasEncorePret =
    bootstrap.siteStatus != null && bootstrap.siteStatus !== 'ready';
  const degrade = !bootstrap.online || pasEncorePret;

  if (degrade) {
    window.setTimeout(() => {
      if (isOpen || speech.style.display === 'block') return;
      showSpeech(
        bootstrap?.online === false
          ? 'Connexion au site momentanément indisponible. Vous pouvez quand même me poser une question.'
          : "Je suis encore en train d'apprendre ce site. Vous pouvez me poser une question, mais je risque d'être moins précis pour l'instant."
      );
    }, 1600);
  } else {
    window.setTimeout(showProactive, 1600);
    window.setInterval(showProactive, 16000);
  }

  return {
    open: () => setOpen(true),
    close: () => {
      setOpen(false);
      clearMarks();
    },
    siteId,
    visitorId: bootstrap?.visitorId,
  };
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Retire toute trace du marqueur d'actions (le visiteur ne doit jamais voir ça). */
function cleanReply(text: string): string {
  const first = text.indexOf('@@ACTIONS@@');
  let out = first === -1 ? text : text.slice(0, first);
  out = out.replace(/@@ACTIONS@@\s*(\[[\s\S]*?\])?/g, '');
  return out.trim();
}

/**
 * Rendu markdown minimal et sûr : on échappe tout le HTML, puis on ré-active
 * uniquement gras, italique, `code`, listes à puces et sauts de ligne.
 */
function renderRich(el: HTMLElement, text: string) {
  const clean = cleanReply(text);
  const lines = clean.split(/\r?\n/);
  const html = lines
    .map((line) => {
      const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
      const body = bullet ? bullet[1] : line;
      let safe = escapeHtml(body);
      safe = safe
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>');
      return bullet ? `•\u00a0${safe}` : safe;
    })
    .join('<br/>');
  el.innerHTML = html;
}

export { registerAction, scrollTo, highlightPhrase as highlightElement, pointTo, navigateTo };
